import React, { useEffect, useRef, useState } from "react";
import { Check, X, Loader2 } from "lucide-react";
import { PasswordInput } from "@/components/PasswordInput";
import { classChecks, contextViolation, scorePassword, checkBreach } from "@/lib/passwordPolicy";

const SCORE_LABELS = ["Too weak", "Too weak", "Weak", "Good", "Strong"];
const SEG_COLORS = ["bg-blaze", "bg-blaze", "bg-amber-500", "bg-lime-500", "bg-volt"];
const LABEL_COLORS = ["text-blaze", "text-blaze", "text-amber-500", "text-lime-500", "text-volt"];

function Rule({ ok, label, testId, highlight }) {
  // Unmet rules go muted by default; after a failed submit (highlight) the unmet ones turn red
  // so the user can see exactly what's blocking them.
  const color = ok ? "text-olive-bright" : highlight ? "text-blaze" : "text-muted-foreground";
  return (
    <li data-testid={testId} data-ok={ok} className={`flex items-center gap-1.5 ${color}`}>
      {ok ? <Check className="w-3.5 h-3.5 shrink-0" strokeWidth={3} /> : <X className={`w-3.5 h-3.5 shrink-0 ${highlight ? "" : "opacity-50"}`} strokeWidth={3} />}
      <span className={highlight && !ok ? "font-bold" : ""}>{label}</span>
    </li>
  );
}

// Reusable strong-password input: show/hide, a live composition CHECKLIST and a
// visibly separate STRENGTH bar (zxcvbn) + HIBP breach check. Reports overall
// validity via onValidityChange so the parent can gate its submit button.
export function PasswordField({
  value,
  onChange,
  id = "password",
  testId = "password-field",
  label = "Password",
  email = "",
  name = "",
  autoComplete = "new-password",
  onValidityChange,
  highlightUnmet = false,
  shakeKey = 0,
}) {
  const [breached, setBreached] = useState(false);
  const [checkingBreach, setCheckingBreach] = useState(false);
  const debounceRef = useRef();
  const cbRef = useRef(onValidityChange);
  cbRef.current = onValidityChange;

  const checks = classChecks(value);
  const classesOk = checks.upper && checks.lower && checks.digit && checks.special;
  const ctx = value ? contextViolation(value, { email, name }) : null;
  const { score, feedback, crackTime } = scorePassword(value, [email, email.split("@")[0] || "", name, "opticswap"]);

  // Everything except the breach check (which is async + debounced).
  const localOk = classesOk && checks.length && !ctx && score >= 3;

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!localOk) {
      setBreached(false);
      setCheckingBreach(false);
      return;
    }
    setCheckingBreach(true);
    debounceRef.current = setTimeout(async () => {
      const b = await checkBreach(value);
      setBreached(b);
      setCheckingBreach(false);
    }, 450);
    return () => clearTimeout(debounceRef.current);
  }, [value, localOk]);

  const valid = localOk && !breached && !checkingBreach;
  useEffect(() => {
    cbRef.current?.(valid);
  }, [valid]);

  const filled = value ? Math.max(1, score) : 0;

  return (
    <div>
      <label htmlFor={id} className="font-mono text-xs uppercase tracking-wide text-secondary-foreground">{label}</label>
      <div className="mt-1">
        <PasswordInput id={id} testId={testId} value={value} onChange={onChange} autoComplete={autoComplete} />
      </div>

      {/* CHECKLIST — the four classes + length. Deliberately separate from the bar. */}
      <div className="mt-2" id={`${testId}-requirements`}>
        <p className={`font-mono text-[10px] uppercase tracking-wide mb-1 ${highlightUnmet ? "text-blaze" : "text-muted-foreground"}`}>Password must include</p>
        <ul key={`chk-${shakeKey}`} data-testid={`${testId}-checklist`}
          className={`grid grid-cols-2 gap-x-3 gap-y-1 text-xs font-mono ${highlightUnmet ? "os-shake" : ""}`}>
          <Rule ok={checks.upper} label="Uppercase" testId="pw-check-upper" highlight={highlightUnmet} />
          <Rule ok={checks.lower} label="Lowercase" testId="pw-check-lower" highlight={highlightUnmet} />
          <Rule ok={checks.digit} label="Number" testId="pw-check-digit" highlight={highlightUnmet} />
          <Rule ok={checks.special} label="Special char" testId="pw-check-special" highlight={highlightUnmet} />
          <Rule ok={checks.length} label="12+ characters" testId="pw-check-length" highlight={highlightUnmet} />
        </ul>
      </div>

      {/* STRENGTH — zxcvbn entropy. This is the gate, even if all boxes are ticked. */}
      {value && (
        <div data-testid={`${testId}-strength`} className="mt-3 border border-sniper bg-obsidian p-2.5">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Strength</span>
            <span data-testid="pw-strength-label" className={`font-mono text-xs uppercase font-bold ${LABEL_COLORS[score]}`}>{SCORE_LABELS[score]}</span>
          </div>
          <div data-testid="pw-strength-bar" className="flex gap-1 mt-1.5">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className={`h-1.5 flex-1 transition-colors ${i < filled ? SEG_COLORS[score] : "bg-sniper"}`} />
            ))}
          </div>
          {crackTime && (
            <p className="mt-2 text-[11px] font-mono text-muted-foreground">
              Est. offline crack time: <span className="text-secondary-foreground">{crackTime}</span>
            </p>
          )}
          {ctx && <p data-testid="pw-context-warning" className="mt-1.5 text-xs font-mono text-blaze">Password can't contain {ctx}.</p>}
          {!ctx && feedback.warning && <p className="mt-1.5 text-xs font-mono text-amber-500">{feedback.warning}</p>}
          {!ctx && !feedback.warning && feedback.suggestions?.[0] && score < 3 && (
            <p className="mt-1.5 text-xs font-mono text-muted-foreground">{feedback.suggestions[0]}</p>
          )}
          {checkingBreach && (
            <p className="mt-1.5 text-xs font-mono text-muted-foreground flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Checking breach databases…</p>
          )}
          {breached && (
            <p data-testid="pw-breach-warning" className="mt-1.5 text-xs font-mono text-blaze">This password has appeared in a known data breach. Choose a different one.</p>
          )}
        </div>
      )}
    </div>
  );
}
